import { useState, useCallback, useRef, ChangeEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Monitor,
  Search,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Building2,
  Eye,
  EyeOff,
  FileText,
  BookOpen,
  Download,
  ChevronDown,
  QrCode,
  Printer,
} from 'lucide-react';
import {
  useEquipments,
  useCreateEquipment,
  useUpdateEquipment,
  useDeleteEquipment,
  useDigitalCentersDropdown,
  type Equipment,
  type EquipmentInput,
} from '@/hooks/useEquipments';
import { useBadgesDropdown } from '@/hooks/useBadges';
import { ApiValidationError } from '@/lib/api';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';
import type { EquipmentEstado, EquipmentRequerFormacao } from 'shared';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const ESTADOS: EquipmentEstado[] = ['Ativo', 'Inativo', 'Em utilização', 'Reservado'];

const CATEGORIAS = [
  'Computador',
  'PS5',
  'Impressora',
  'Maquina de corte',
  'Laser',
  'Outro',
];

const REQUER_FORMACAO: EquipmentRequerFormacao[] = ['Baixa', 'Média', 'Alta'];

const EMPTY_FORM: EquipmentInput = {
  digitalCenterID: 0,
  equipamento: '',
  tipoCategoria: '',
  numeroSerie: null,
  estado: 'Ativo',
  descricao: '',
  slotUtilizacao: null,
  quantidade: 1,
  comoUtilizar: null,
  manualUtilizador: null,
  requerFormacao: 'Baixa',
  descricaoFormacao: null,
  badgeId: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Validation ───────────────────────────────────────────────────────────────

type FormErrors = Partial<Record<keyof EquipmentInput, string>>;

function validate(form: EquipmentInput): FormErrors {
  const e: FormErrors = {};

  if (!form.digitalCenterID || form.digitalCenterID <= 0)
    e.digitalCenterID = 'O centro digital é obrigatório.';

  if (!form.equipamento.trim()) e.equipamento = 'O nome do equipamento é obrigatório.';
  else if (form.equipamento.length > 50) e.equipamento = 'Máximo de 50 caracteres.';

  if (!form.tipoCategoria.trim()) e.tipoCategoria = 'O tipo/categoria é obrigatório.';
  else if (form.tipoCategoria.length > 50) e.tipoCategoria = 'Máximo de 50 caracteres.';

  if (!form.estado) e.estado = 'O estado é obrigatório.';

  if (!form.descricao.trim()) e.descricao = 'A descrição é obrigatória.';
  else if (form.descricao.length > 150) e.descricao = 'Máximo de 150 caracteres.';

  if (form.slotUtilizacao !== null && form.slotUtilizacao !== undefined) {
    if (form.slotUtilizacao < 0 || form.slotUtilizacao > 999)
      e.slotUtilizacao = 'Indicar valor em minutos.';
  }

  const quantidade = form.quantidade ?? 1;
  if (!Number.isInteger(quantidade) || quantidade < 1 || quantidade > 999)
    e.quantidade = 'Indicar um número inteiro entre 1 e 999.';

  return e;
}

// ─── Estado Badge ─────────────────────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: EquipmentEstado }) {
  const styles: Record<EquipmentEstado, string> = {
    'Ativo': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'Inativo': 'bg-muted text-muted-foreground',
    'Em utilização': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    'Reservado': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[estado] ?? 'bg-muted text-muted-foreground'}`}>
      {estado}
    </span>
  );
}

// ─── Formação Badge ───────────────────────────────────────────────────────────

function FormacaoBadge({ nivel }: { nivel: EquipmentRequerFormacao }) {
  const styles: Record<EquipmentRequerFormacao, string> = {
    'Baixa':  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'Média':  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    'Alta':   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[nivel] ?? 'bg-muted text-muted-foreground'}`}>
      {nivel}
    </span>
  );
}

// ─── Form Field ───────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

function FormField({ label, error, required, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground/60">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Create / Edit Modal ──────────────────────────────────────────────────────

interface EquipmentModalProps {
  mode: 'create' | 'edit';
  initial?: Equipment;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function EquipmentModal({ mode, initial, onClose, onSuccess, onError }: EquipmentModalProps) {
  const create = useCreateEquipment();
  const update = useUpdateEquipment();
  const { data: centers = [], isLoading: centersLoading } = useDigitalCentersDropdown();
  const { data: badges = [] } = useBadgesDropdown();

  const [form, setForm] = useState<EquipmentInput>(
    initial
      ? {
          digitalCenterID: initial.digitalCenterID,
          equipamento: initial.equipamento,
          tipoCategoria: initial.tipoCategoria,
          numeroSerie: initial.numeroSerie ?? null,
          estado: initial.estado,
          descricao: initial.descricao,
          slotUtilizacao: initial.slotUtilizacao ?? null,
          quantidade: initial.quantidade ?? 1,
          comoUtilizar: initial.comoUtilizar ?? null,
          manualUtilizador: initial.manualUtilizador ?? null,
          requerFormacao: initial.requerFormacao,
          descricaoFormacao: initial.descricaoFormacao ?? null,
          badgeId: initial.badgeId ?? null,
        }
      : EMPTY_FORM,
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [serverErrors, setServerErrors] = useState<FormErrors>({});
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const isPending = create.isPending || update.isPending;

  const fieldError = (key: keyof EquipmentInput) => errors[key] || serverErrors[key];

  const set = <K extends keyof EquipmentInput>(key: K, value: EquipmentInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    if (serverErrors[key]) setServerErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const handlePdfChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setErrors(prev => ({ ...prev, manualUtilizador: 'Apenas ficheiros PDF são permitidos.' }));
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, manualUtilizador: 'O ficheiro PDF não pode exceder 4MB.' }));
      return;
    }
    setPdfLoading(true);
    try {
      const base64 = await readFileAsBase64(file);
      set('manualUtilizador', base64);
      setPdfFileName(file.name);
      setErrors(prev => ({ ...prev, manualUtilizador: undefined }));
    } catch {
      setErrors(prev => ({ ...prev, manualUtilizador: 'Erro ao ler o ficheiro.' }));
    } finally {
      setPdfLoading(false);
    }
  };

  const removePdf = () => {
    set('manualUtilizador', null);
    setPdfFileName(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: EquipmentInput = {
      ...form,
      equipamento: form.equipamento.trim(),
      tipoCategoria: form.tipoCategoria.trim(),
      descricao: form.descricao.trim(),
      numeroSerie: form.numeroSerie?.trim() || null,
      comoUtilizar: form.comoUtilizar?.trim() || null,
      descricaoFormacao: form.descricaoFormacao?.trim() || null,
      badgeId: form.badgeId || null,
    };

    const clientErrors = validate(trimmed);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    try {
      if (mode === 'create') {
        await create.mutateAsync(trimmed);
        onSuccess('Equipamento criado com sucesso.');
      } else {
        await update.mutateAsync({ id: initial!.id, data: trimmed });
        onSuccess('Equipamento actualizado com sucesso.');
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setServerErrors(err.errors as FormErrors);
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro desconhecido.');
    }
  };

  const title = mode === 'create' ? 'Novo Equipamento' : 'Editar Equipamento';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eq-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Monitor className="w-5 h-5 text-primary" />
            <h2 id="eq-modal-title" className="text-lg font-semibold text-foreground">{title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form id="eq-form" onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4 overflow-y-auto">

          {/* Centro Digital */}
          <FormField label="Centro Digital" required error={fieldError('digitalCenterID')}>
            <div className="relative">
              <select
                className={`input appearance-none pr-8 ${fieldError('digitalCenterID') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.digitalCenterID || ''}
                onChange={e => set('digitalCenterID', Number(e.target.value))}
                disabled={centersLoading}
                aria-invalid={!!fieldError('digitalCenterID')}
              >
                <option value="">Selecionar centro digital…</option>
                {centers.map(c => (
                  <option key={c.id} value={c.id}>{c.centerName}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            {/* Equipamento */}
            <FormField label="Nome do Equipamento" required error={fieldError('equipamento')}>
              <input
                className={`input ${fieldError('equipamento') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.equipamento}
                onChange={e => set('equipamento', e.target.value)}
                maxLength={50}
                placeholder="ex. Portátil HP EliteBook"
                aria-invalid={!!fieldError('equipamento')}
              />
              <div className="flex justify-end">
                <span className="text-[11px] text-muted-foreground/50 tabular-nums">{form.equipamento.length}/50</span>
              </div>
            </FormField>

            {/* Tipo / Categoria */}
            <FormField label="Tipo / Categoria" required error={fieldError('tipoCategoria')}>
              <div className="relative">
                <input
                  list="categorias-list"
                  className={`input ${fieldError('tipoCategoria') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                  value={form.tipoCategoria}
                  onChange={e => set('tipoCategoria', e.target.value)}
                  maxLength={50}
                  placeholder="ex. Computador"
                  aria-invalid={!!fieldError('tipoCategoria')}
                />
                <datalist id="categorias-list">
                  {CATEGORIAS.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="flex justify-end">
                <span className="text-[11px] text-muted-foreground/50 tabular-nums">{form.tipoCategoria.length}/50</span>
              </div>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Número de Série */}
            <FormField label="Número de Série" error={fieldError('numeroSerie')}>
              <input
                className="input"
                value={form.numeroSerie ?? ''}
                onChange={e => set('numeroSerie', e.target.value || null)}
                maxLength={100}
                placeholder="ex. SN-12345-ABC"
              />
            </FormField>

            {/* Estado */}
            <FormField label="Estado" required error={fieldError('estado')}>
              <div className="relative">
                <select
                  className={`input appearance-none pr-8 ${fieldError('estado') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                  value={form.estado}
                  onChange={e => set('estado', e.target.value as EquipmentEstado)}
                  aria-invalid={!!fieldError('estado')}
                >
                  {ESTADOS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </FormField>
          </div>

          {/* Descrição */}
          <FormField label="Descrição" required error={fieldError('descricao')}>
            <textarea
              className={`input resize-none h-20 ${fieldError('descricao') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.descricao}
              onChange={e => set('descricao', e.target.value)}
              maxLength={150}
              placeholder="Breve descrição do equipamento…"
              aria-invalid={!!fieldError('descricao')}
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/50 tabular-nums">{form.descricao.length}/150</span>
            </div>
          </FormField>

          {/* Slot de Utilização */}
          <FormField label="Tempo previsto de utilização" error={fieldError('slotUtilizacao')} hint="Em minutos. Passado este tempo, o equipamento aparece em atraso até ser devolvido.">
            <input
              type="number"
              className={`input w-32 ${fieldError('slotUtilizacao') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.slotUtilizacao ?? ''}
              onChange={e => set('slotUtilizacao', e.target.value === '' ? null : Number(e.target.value))}
              min={0}
              max={999}
              placeholder="ex. 60"
            />
          </FormField>

          {/* Quantidade */}
          <FormField
            label="Quantidade"
            required
            error={fieldError('quantidade')}
            hint="Unidades idênticas partilhando este registo e este QR (ex. 20 micro:bits numa caixa). Cada participante ocupa uma unidade."
          >
            <input
              type="number"
              className={`input w-32 ${fieldError('quantidade') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.quantidade ?? 1}
              onChange={e => set('quantidade', e.target.value === '' ? 1 : Number(e.target.value))}
              min={1}
              max={999}
              step={1}
            />
          </FormField>

          {/* Como Utilizar */}
          <FormField label="Como Utilizar" error={fieldError('comoUtilizar')} hint="Instruções de uso (texto livre).">
            <textarea
              className="input resize-none h-24"
              value={form.comoUtilizar ?? ''}
              onChange={e => set('comoUtilizar', e.target.value || null)}
              placeholder="Descreve como utilizar este equipamento…"
            />
          </FormField>

          {/* Manual do Utilizador (PDF) */}
          <FormField label="Manual do Utilizador (PDF)" error={fieldError('manualUtilizador')} hint="Máximo 4MB. Formato PDF.">
            {form.manualUtilizador ? (
              <div className="flex items-center gap-2 p-2.5 rounded-md border border-border bg-muted/30">
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm text-foreground flex-1 truncate">
                  {pdfFileName ?? 'Manual carregado'}
                </span>
                <button
                  type="button"
                  onClick={removePdf}
                  className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded"
                  aria-label="Remover PDF"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className={`flex items-center gap-2 p-2.5 rounded-md border border-dashed border-border bg-muted/10 cursor-pointer hover:bg-accent/30 transition-colors ${pdfLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                {pdfLoading
                  ? <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                  : <FileText className="w-4 h-4 text-muted-foreground" />
                }
                <span className="text-sm text-muted-foreground">
                  {pdfLoading ? 'A carregar…' : 'Clica para selecionar PDF'}
                </span>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  className="sr-only"
                  onChange={handlePdfChange}
                  disabled={pdfLoading}
                />
              </label>
            )}
          </FormField>

          {/* Requer Formação */}
          <FormField label="Nível de Formação" required error={fieldError('requerFormacao')} hint="Seleciona o nível de formação necessário para utilizar este equipamento.">
            <div className="relative">
              <select
                className={`input appearance-none pr-8 ${fieldError('requerFormacao') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.requerFormacao}
                onChange={e => set('requerFormacao', e.target.value as EquipmentRequerFormacao)}
                aria-invalid={!!fieldError('requerFormacao')}
              >
                {REQUER_FORMACAO.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </FormField>

          {/* Descrição de Formação (conditional) */}
          {form.requerFormacao !== 'Baixa' && (
            <FormField label="Descrição / Link de Formação" error={fieldError('descricaoFormacao')} hint="Descreve a formação necessária ou indica um link (opcional).">
              <textarea
                className={`input resize-none h-20 ${fieldError('descricaoFormacao') ? 'border-destructive focus:ring-destructive/30' : ''}`}
                value={form.descricaoFormacao ?? ''}
                onChange={e => set('descricaoFormacao', e.target.value || null)}
                placeholder="ex. Formação obrigatória em segurança informática — ver https://…"
                aria-invalid={!!fieldError('descricaoFormacao')}
              />
            </FormField>
          )}

          {/* Badge (optional association) */}
          <FormField label="Credencial Associada" hint="Badge obrigatório para utilizar este equipamento (opcional).">
            <div className="relative">
              <select
                className="input appearance-none pr-8"
                value={form.badgeId ?? ''}
                onChange={e => set('badgeId', e.target.value || null)}
              >
                <option value="">Sem credencial</option>
                {badges.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </FormField>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="eq-form"
            disabled={isPending || pdfLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'create' ? 'Criar Equipamento' : 'Guardar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Dialog ────────────────────────────────────────────────────────────

interface DeleteDialogProps {
  equipment: Equipment;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function DeleteDialog({ equipment, onClose, onSuccess, onError }: DeleteDialogProps) {
  const del = useDeleteEquipment();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const confirm = async () => {
    if (!reason.trim()) {
      setReasonError('O motivo de eliminação é obrigatório.');
      return;
    }
    try {
      await del.mutateAsync({ id: equipment.id, reason: reason.trim() });
      onSuccess(`"${equipment.equipamento}" eliminado com sucesso.`);
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError && err.errors['reason']) {
        setReasonError(err.errors['reason']);
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro ao eliminar.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="del-eq-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <h3 id="del-eq-title" className="text-base font-semibold text-foreground">
              Eliminar Equipamento
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Tens a certeza que queres eliminar{' '}
              <span className="font-semibold text-foreground">"{equipment.equipamento}"</span>?
              O registo ficará arquivado e não poderá ser editado.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Motivo de eliminação<span className="text-destructive ml-0.5">*</span>
          </label>
          <textarea
            className={`input resize-none h-20 ${reasonError ? 'border-destructive focus:ring-destructive/30' : ''}`}
            value={reason}
            onChange={e => { setReason(e.target.value); if (reasonError) setReasonError(''); }}
            placeholder="Descreve o motivo de eliminação…"
            aria-invalid={!!reasonError}
          />
          {reasonError && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              {reasonError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={del.isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={del.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 transition-colors"
          >
            {del.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

interface DetailPanelProps {
  equipment: Equipment;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function DetailPanel({ equipment, onClose, onEdit, onDelete }: DetailPanelProps) {
  const isDeleted = !!equipment.deletedAt;

  const openPdf = () => {
    if (!equipment.manualUtilizador) return;
    const win = window.open();
    if (win) {
      win.document.write(
        `<iframe src="${equipment.manualUtilizador}" style="width:100%;height:100vh;border:none;"></iframe>`,
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      aria-label="Detalhe do equipamento"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-lg bg-card shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Monitor className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground leading-tight">
                {equipment.equipamento}
              </h2>
              <p className="text-xs text-muted-foreground">{equipment.tipoCategoria}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar painel"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Deleted banner */}
          {isDeleted && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-destructive">Registo eliminado</p>
                <p className="text-xs text-destructive/80 mt-0.5">
                  {equipment.deletionReason}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Em {formatDate(equipment.deletedAt!)}
                </p>
              </div>
            </div>
          )}

          {/* Status + Centro */}
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-40 p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground mb-1 uppercase tracking-wide">Estado</p>
              <EstadoBadge estado={equipment.estado} />
            </div>
            <div className="flex-1 min-w-40 p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground mb-1 uppercase tracking-wide">Centro Digital</p>
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground/60" />
                <span className="text-sm font-medium text-foreground">{equipment.digitalCenterName}</span>
              </div>
            </div>
          </div>

          {/* Core fields */}
          <div className="space-y-3">
            <DetailRow label="Nº de Série" value={equipment.numeroSerie ?? '—'} />
            <DetailRow label="Tempo previsto de utilização" value={equipment.slotUtilizacao ? `${equipment.slotUtilizacao} min` : 'Sem limite'} />
            <DetailRow label="Unidades" value={`${Math.max(0, equipment.quantidade - equipment.emUso)} livres de ${equipment.quantidade}`} />
          </div>

          {/* Descrição */}
          <DetailSection label="Descrição" icon={<FileText className="w-4 h-4" />}>
            <p className="text-sm text-foreground">{equipment.descricao}</p>
          </DetailSection>

          {/* Como Utilizar */}
          {equipment.comoUtilizar && (
            <DetailSection label="Como Utilizar" icon={<BookOpen className="w-4 h-4" />}>
              <p className="text-sm text-foreground whitespace-pre-line">{equipment.comoUtilizar}</p>
            </DetailSection>
          )}

          {/* Manual do Utilizador */}
          {equipment.manualUtilizador && (
            <DetailSection label="Manual do Utilizador" icon={<FileText className="w-4 h-4" />}>
              <button
                onClick={openPdf}
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/20 hover:bg-accent text-sm text-foreground transition-colors"
              >
                <Download className="w-4 h-4 text-primary" />
                Visualizar PDF
              </button>
            </DetailSection>
          )}

          {/* Formação */}
          <div className="p-3 rounded-lg border border-border bg-muted/10">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-2">Nível de Formação</p>
            <FormacaoBadge nivel={equipment.requerFormacao} />
            {equipment.requerFormacao !== 'Baixa' && equipment.descricaoFormacao && (
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">
                {equipment.descricaoFormacao}
              </p>
            )}
          </div>

          {/* Badge association */}
          {equipment.badgeId && (
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-2">Credencial Associada</p>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                {equipment.badgeName ?? equipment.badgeId}
              </span>
            </div>
          )}

          {/* QR Code */}
          <div className="p-3 rounded-lg border border-border bg-muted/10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <QrCode className="w-4 h-4 text-muted-foreground" />
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">QR Code</p>
              </div>
              {equipment.qrCode && (
                <button
                  onClick={() => {
                    const a = document.createElement('a');
                    a.href = equipment.qrCode!;
                    a.download = `qrcode-equipment-${equipment.id}-${equipment.equipamento.replace(/\s+/g, '-').toLowerCase()}.png`;
                    a.click();
                  }}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                  title="Descarregar QR Code"
                >
                  <Download className="w-3.5 h-3.5" />
                  Descarregar
                </button>
              )}
            </div>
            {equipment.qrCode ? (
              <div className="flex flex-col items-center gap-2">
                <div className="p-2 rounded-md border border-border bg-white inline-block">
                  <img
                    src={equipment.qrCode}
                    alt={`QR Code de ${equipment.equipamento}`}
                    className="w-36 h-36 object-contain"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground/60 text-center">
                  <code className="font-mono">AICD:equipment:{equipment.id}</code>
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 gap-2">
                <QrCode className="w-8 h-8 text-muted-foreground/20" />
                <p className="text-xs text-muted-foreground/50">QR Code não disponível</p>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Criado em</p>
              <p className="text-xs text-foreground">{formatDate(equipment.createdAt)}</p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Actualizado em</p>
              <p className="text-xs text-foreground">{formatDate(equipment.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Footer — actions (hidden for deleted records) */}
        {!isDeleted && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-border shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-md border border-destructive/30 bg-destructive/5 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className="text-xs text-foreground text-right">{value}</span>
    </div>
  );
}

function DetailSection({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="pl-5">{children}</div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ search, onCreate }: { search: string; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
        <Monitor className="w-7 h-7 text-muted-foreground/40" />
      </div>
      {search ? (
        <>
          <p className="text-base font-medium text-muted-foreground">
            Nenhum resultado para <span className="text-foreground">"{search}"</span>
          </p>
          <p className="text-sm text-muted-foreground/60">Tenta pesquisar por outro nome.</p>
        </>
      ) : (
        <>
          <p className="text-base font-medium text-muted-foreground">Sem equipamentos registados</p>
          <button
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Criar primeiro equipamento
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; equipment: Equipment }
  | { type: 'delete'; equipment: Equipment };

export default function EquipamentosPage() {
  const { toasts, toast, dismiss } = useToast();
  const { data: centers = [] } = useDigitalCentersDropdown();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCenterID, setFilterCenterID] = useState<number>(0);
  const [filterEstado, setFilterEstado] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const { data, isLoading, isError, error, isFetching } = useEquipments({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch,
    digitalCenterID: filterCenterID || undefined,
    estado: filterEstado || undefined,
    showDeleted,
  });

  const equipments = data?.equipments ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);

  const closeModal = () => setModal({ type: 'none' });

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setFilterCenterID(0);
    setFilterEstado('');
    setPage(0);
  };

  const hasFilters = !!debouncedSearch || filterCenterID > 0 || !!filterEstado;

  return (
    <>
      <Helmet>
        <title>Equipamentos | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Monitor className="w-6 h-6 text-primary" />
              Equipamentos
            </h1>
            <p className="text-muted-foreground mt-1">Gestão do parque de equipamentos.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Carries the centre being looked at, so the sheet opens on it. */}
            <Link
              to={
                filterCenterID > 0
                  ? `/equipamentos/etiquetas?centro=${filterCenterID}`
                  : '/equipamentos/etiquetas'
              }
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Printer className="w-4 h-4" />
              Imprimir etiquetas
            </Link>
            <button
              onClick={() => setModal({ type: 'create' })}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Novo Equipamento
            </button>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative min-w-56 max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              placeholder="Pesquisar por nome…"
              className="input pl-9 w-full"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              aria-label="Pesquisar equipamentos"
            />
          </div>

          {/* Centro Digital filter */}
          <div className="relative">
            <select
              className="input appearance-none pr-8 min-w-44"
              value={filterCenterID}
              onChange={e => { setFilterCenterID(Number(e.target.value)); setPage(0); }}
              aria-label="Filtrar por centro digital"
            >
              <option value={0}>Todos os centros</option>
              {centers.map(c => <option key={c.id} value={c.id}>{c.centerName}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>

          {/* Estado filter */}
          <div className="relative">
            <select
              className="input appearance-none pr-8"
              value={filterEstado}
              onChange={e => { setFilterEstado(e.target.value); setPage(0); }}
              aria-label="Filtrar por estado"
            >
              <option value="">Todos os estados</option>
              {ESTADOS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          </div>

          {/* Show deleted toggle */}
          <button
            onClick={() => { setShowDeleted(v => !v); setPage(0); }}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${showDeleted ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'}`}
            aria-pressed={showDeleted}
          >
            {showDeleted ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showDeleted ? 'Ocultar eliminados' : 'Mostrar eliminados'}
          </button>

          {/* Count + clear filters */}
          <div className="flex items-center gap-2 ml-auto">
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              >
                Limpar filtros
              </button>
            )}
            {total > 0 && (
              <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                {total} equipamento{total !== 1 ? 's' : ''}
                {isFetching && !isLoading && (
                  <Loader2 className="inline-block w-3 h-3 ml-1.5 animate-spin text-primary" />
                )}
              </span>
            )}
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="card overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <AlertTriangle className="w-8 h-8 text-destructive" />
              <p className="text-sm font-medium text-destructive">
                {error?.message ?? 'Erro ao carregar equipamentos.'}
              </p>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {equipments.length === 0 ? (
                <EmptyState
                  search={debouncedSearch}
                  onCreate={() => setModal({ type: 'create' })}
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm" aria-label="Lista de equipamentos">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Nome</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Categoria</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Centro Digital</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Estado</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground" title="Unidades livres / total">Unidades</th>
                        <th scope="col" className="px-4 py-3 w-20 text-right font-medium text-muted-foreground">Acções</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {equipments.map(eq => {
                        const isDeleted = !!eq.deletedAt;
                        return (
                          <tr
                            key={eq.id}
                            className={`hover:bg-accent/30 transition-colors cursor-pointer ${isDeleted ? 'opacity-60' : ''}`}
                            onClick={() => setSelectedEquipment(eq)}
                          >
                            {/* Name */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                                  <Monitor className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">{eq.equipamento}</p>
                                  {eq.numeroSerie && (
                                    <p className="text-xs text-muted-foreground/60">{eq.numeroSerie}</p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Categoria */}
                            <td className="px-4 py-3 text-muted-foreground">{eq.tipoCategoria}</td>

                            {/* Centro */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1.5">
                                <Building2 className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
                                <span className="text-muted-foreground">{eq.digitalCenterName}</span>
                              </div>
                            </td>

                            {/* Estado */}
                            <td className="px-4 py-3">
                              {isDeleted
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Eliminado</span>
                                : <EstadoBadge estado={eq.estado} />
                              }
                            </td>

                            {/* Unidades livres / total */}
                            <td className="px-4 py-3 text-muted-foreground tabular-nums">
                              {Math.max(0, eq.quantidade - eq.emUso)}/{eq.quantidade}
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div
                                className="flex items-center gap-1 justify-end"
                                onClick={e => e.stopPropagation()}
                              >
                                {!isDeleted && (
                                  <>
                                    <button
                                      onClick={() => setModal({ type: 'edit', equipment: eq })}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                                      title={`Editar ${eq.equipamento}`}
                                      aria-label={`Editar ${eq.equipamento}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setModal({ type: 'delete', equipment: eq })}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                      title={`Eliminar ${eq.equipamento}`}
                                      aria-label={`Eliminar ${eq.equipamento}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Pagination ── */}
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {from}–{to} de {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      aria-label="Página anterior"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let p: number;
                        if (totalPages <= 5) p = i;
                        else if (page < 3) p = i;
                        else if (page >= totalPages - 3) p = totalPages - 5 + i;
                        else p = page - 2 + i;
                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            aria-label={`Página ${p + 1}`}
                            aria-current={p === page ? 'page' : undefined}
                            className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${p === page ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
                          >
                            {p + 1}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      aria-label="Próxima página"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Detail Panel ── */}
      {selectedEquipment && (
        <DetailPanel
          equipment={selectedEquipment}
          onClose={() => setSelectedEquipment(null)}
          onEdit={() => {
            setModal({ type: 'edit', equipment: selectedEquipment });
            setSelectedEquipment(null);
          }}
          onDelete={() => {
            setModal({ type: 'delete', equipment: selectedEquipment });
            setSelectedEquipment(null);
          }}
        />
      )}

      {/* ── Modals ── */}
      {modal.type === 'create' && (
        <EquipmentModal
          mode="create"
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'edit' && (
        <EquipmentModal
          mode="edit"
          initial={modal.equipment}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'delete' && (
        <DeleteDialog
          equipment={modal.equipment}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
